import { Head, Link, router } from "@inertiajs/react"
import { useState, useCallback } from "react"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface AdminAccount {
  id: number
  name: string
  account_type: string
  credit_balance: number
  peak_credit_balance: number
  stripe_customer_id: string | null
  logo_url: string | null
  created_at: string
}

interface AdminSubscription {
  plan_tier: string
  plan_name: string | null
  status: string
  current_period_start: string | null
  current_period_end: string | null
  stripe_subscription_id: string | null
  cancelled_at: string | null
}

interface Transaction {
  id: number
  transaction_type: string
  amount: number
  balance_before: number
  balance_after: number
  description: string | null
  by: string | null
  created_at: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  account: AdminAccount
  subscription: AdminSubscription | null
  transactions: Transaction[]
  tx_pagination: PaginationMeta
  tx_sort: string
  tx_dir: string
  tx_type: string | null
  tx_q: string | null
}

type DialogMode = "topup" | "deduction" | "set_balance" | null

const TYPE_BADGES: Record<string, { className: string; label: string }> = {
  topup:      { className: "border-emerald-500/20 bg-emerald-500/10 text-emerald-400", label: "Top-up" },
  deduction:  { className: "border-red/20 bg-red/10 text-red", label: "Deduction" },
  adjustment: { className: "border-blue-400/20 bg-blue-400/10 text-blue-400", label: "Adjustment" },
}

const TYPE_FILTERS = [
  { value: "all",        label: "All types" },
  { value: "deduction",  label: "Deductions" },
  { value: "topup",      label: "Top-ups" },
  { value: "adjustment", label: "Adjustments" },
] as const

const PLAN_TIER_STYLES: Record<string, string> = {
  free:       "border-white/10 bg-white/5 text-cream/40",
  starter:    "border-blue-500/30 bg-blue-500/10 text-blue-400",
  pro:        "border-violet-500/30 bg-violet-500/10 text-violet-400",
  enterprise: "border-amber-500/30 bg-amber-500/10 text-amber-400",
}

const SUB_STATUS_STYLES: Record<string, string> = {
  active:    "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  past_due:  "border-orange-500/30 bg-orange-500/10 text-orange-400",
  cancelled: "border-red/20 bg-red/10 text-red",
  expired:   "border-white/10 bg-white/5 text-cream/30",
}

export default function AdminAccountShow({
  account,
  subscription,
  transactions,
  tx_pagination,
  tx_sort,
  tx_dir,
  tx_type,
  tx_q,
}: Props) {
  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [search, setSearch] = useState(tx_q || "")

  const initials = account.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  function handleSubmit() {
    if (!dialogMode) return
    router.post(`/admin/accounts/${account.id}/update_credits`, {
      action_type: dialogMode,
      amount: Number(amount),
      description: description || undefined,
    }, {
      onSuccess: () => {
        setDialogMode(null)
        setAmount("")
        setDescription("")
      },
    })
  }

  function openDialog(mode: DialogMode) {
    setDialogMode(mode)
    setAmount("")
    setDescription("")
  }

  const txParams = useCallback((): Record<string, string> => {
    const p: Record<string, string> = {}
    if (tx_type) p.type = tx_type
    if (tx_q)    p.q = tx_q
    if (tx_sort !== "created_at") p.tx_sort = tx_sort
    if (tx_dir !== "desc")        p.tx_dir = tx_dir
    return p
  }, [tx_type, tx_q, tx_sort, tx_dir])

  const navigate = useCallback((overrides: Record<string, string | undefined>) => {
    const params = txParams()
    Object.entries(overrides).forEach(([k, v]) => {
      if (v) params[k] = v
      else delete params[k]
    })
    router.get(`/admin/accounts/${account.id}`, params, {
      preserveState: true,
      preserveScroll: true,
    })
  }, [account.id, txParams])

  function handleSort(column: string) {
    const newDir = tx_sort === column && tx_dir === "desc" ? "asc" : "desc"
    navigate({
      tx_sort: column === "created_at" ? undefined : column,
      tx_dir:  newDir === "desc" ? undefined : newDir,
      tx_page: undefined,
    })
  }

  function handleTypeFilter(value: string) {
    navigate({ type: value === "all" ? undefined : value, tx_page: undefined })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    navigate({ q: search.trim() || undefined, tx_page: undefined })
  }

  return (
    <AdminLayout>
      <Head title={`${account.name} — Accounts — Admin`} />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/accounts" className="text-cream/40 hover:text-cream transition-colors">
              <ArrowLeftIcon className="size-5" />
            </Link>
            <Avatar className="size-12 rounded-lg">
              <AvatarImage src={account.logo_url || undefined} className="object-contain" />
              <AvatarFallback className="rounded-lg bg-white/10 text-sm text-cream/60">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-display font-semibold text-cream">{account.name}</h1>
              <div className="mt-0.5 flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={
                    account.account_type === "agency"
                      ? "border-blue-500/30 bg-blue-500/10 text-blue-400"
                      : "border-white/10 bg-white/5 text-cream/50"
                  }
                >
                  {account.account_type}
                </Badge>
                <span className="text-xs text-cream/40">
                  Created {new Date(account.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit", month: "short", year: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-white/10 text-cream/70 hover:text-cream"
            onClick={() => router.post(`/admin/accounts/${account.id}/impersonate`)}
          >
            Switch to account
          </Button>
        </div>

        {/* Stats row */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Credit balance */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-cream/40">Credit balance</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-cream">
                  {account.credit_balance.toLocaleString()}
                </p>
                {account.peak_credit_balance > 0 && (
                  <p className="mt-0.5 text-[11px] text-cream/30">
                    Peak: {account.peak_credit_balance.toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  size="sm"
                  onClick={() => openDialog("topup")}
                >
                  Add credits
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-cream/70 hover:text-cream"
                  onClick={() => openDialog("deduction")}
                >
                  Deduct
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-cream/70 hover:text-cream"
                  onClick={() => openDialog("set_balance")}
                >
                  Set balance
                </Button>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
            <p className="text-[11px] font-medium uppercase tracking-wider text-cream/40">Subscription</p>
            {subscription && subscription.plan_tier !== "free" ? (
              <div className="mt-2 space-y-3">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={PLAN_TIER_STYLES[subscription.plan_tier] ?? "border-white/10 bg-white/5 text-cream/50"}
                  >
                    {subscription.plan_name ?? subscription.plan_tier}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={SUB_STATUS_STYLES[subscription.status] ?? "border-white/10 bg-white/5 text-cream/40"}
                  >
                    {subscription.status.replace("_", " ")}
                  </Badge>
                </div>
                {subscription.current_period_start && subscription.current_period_end && (
                  <p className="text-xs text-cream/40">
                    {new Date(subscription.current_period_start).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    {" → "}
                    {new Date(subscription.current_period_end).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                )}
                {subscription.cancelled_at && (
                  <p className="text-xs text-red/70">
                    Cancelled {new Date(subscription.cancelled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                )}
                {subscription.stripe_subscription_id && (
                  <p className="font-mono text-[10px] text-cream/20 break-all">{subscription.stripe_subscription_id}</p>
                )}
              </div>
            ) : (
              <div className="mt-2">
                <Badge variant="outline" className="border-white/10 bg-white/5 text-cream/30">Free</Badge>
                {account.stripe_customer_id && (
                  <p className="mt-2 font-mono text-[10px] text-cream/20">{account.stripe_customer_id}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Transaction history */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-cream/40">
              Transactions
              <span className="ml-1.5 text-cream/20">({tx_pagination.count})</span>
            </h2>
            <div className="flex items-center gap-2">
              <form onSubmit={handleSearch} className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-cream/30" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search descriptions..."
                  className="h-8 w-48 rounded-md border border-white/10 bg-white/5 pl-8 pr-3 text-xs text-cream placeholder:text-cream/30 focus:border-white/20 focus:outline-none"
                />
              </form>
              <Select value={tx_type || "all"} onValueChange={handleTypeFilter}>
                <SelectTrigger className="h-8 w-[130px] border-white/10 bg-white/5 text-xs text-cream/70">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_FILTERS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                  <SortHeader label="Date"    column="created_at"   current={tx_sort} direction={tx_dir} onSort={handleSort} />
                  <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Type</th>
                  <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">Description</th>
                  <SortHeader label="Amount"  column="amount"        current={tx_sort} direction={tx_dir} onSort={handleSort} className="text-right" />
                  <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40 text-right">Before</th>
                  <SortHeader label="After"   column="balance_after" current={tx_sort} direction={tx_dir} onSort={handleSort} className="text-right" />
                  <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40">By</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-xs text-cream/30">
                      {tx_type || tx_q ? "No matching transactions" : "No transactions yet"}
                    </td>
                  </tr>
                )}
                {transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-white/[0.04] text-cream/70">
                    <td className="whitespace-nowrap px-4 py-2 text-xs tabular-nums text-cream/40">
                      {new Date(tx.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit", month: "short", year: "numeric",
                      })}{" "}
                      <span className="text-cream/20">
                        {new Date(tx.created_at).toLocaleTimeString("en-GB", {
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      {(() => {
                        const badge = TYPE_BADGES[tx.transaction_type]
                        return (
                          <Badge variant="outline" className={`text-[10px] ${badge?.className || "border-white/10 text-cream/50"}`}>
                            {badge?.label || tx.transaction_type}
                          </Badge>
                        )
                      })()}
                    </td>
                    <td className="max-w-[240px] truncate px-4 py-2 text-xs text-cream/40">
                      {tx.description || "\u2014"}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-2 text-right text-xs tabular-nums font-medium ${
                      tx.amount > 0 ? "text-emerald-400" : "text-red"
                    }`}>
                      {tx.amount > 0 ? "+" : ""}{tx.amount.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-xs tabular-nums text-cream/30">
                      {tx.balance_before.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-xs tabular-nums text-cream/50">
                      {tx.balance_after.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-cream/30">
                      {tx.by === "Stripe"
                        ? <span className="font-medium text-violet-400/70">Stripe</span>
                        : tx.by || "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            baseUrl={`/admin/accounts/${account.id}`}
            params={txParams()}
            pagination={tx_pagination}
            pageParam="tx_page"
          />
        </div>
      </div>

      {/* Credit dialog */}
      <Dialog open={!!dialogMode} onOpenChange={(open) => !open && setDialogMode(null)}>
        <DialogContent className="bg-charcoal border-white/10 text-cream">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === "topup" ? "Add credits" : dialogMode === "deduction" ? "Deduct credits" : "Set balance"}
            </DialogTitle>
            <DialogDescription className="text-cream/50">
              {dialogMode === "topup"
                ? `Add credits to ${account.name}. Current balance: ${account.credit_balance.toLocaleString()}`
                : dialogMode === "deduction"
                  ? `Deduct credits from ${account.name}. Current balance: ${account.credit_balance.toLocaleString()}`
                  : `Set an exact balance for ${account.name}. Current balance: ${account.credit_balance.toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-cream/70">
                {dialogMode === "topup" ? "Amount to add" : dialogMode === "deduction" ? "Amount to deduct" : "New balance"}
              </Label>
              <Input
                type="number"
                min={dialogMode === "set_balance" ? 0 : 1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={dialogMode === "deduction" ? "e.g. 500" : dialogMode === "topup" ? "e.g. 5000" : "e.g. 10000"}
                className="mt-1 border-white/10 bg-white/5 text-cream"
              />
            </div>
            <div>
              <Label className="text-cream/70">Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={dialogMode === "deduction" ? "e.g. Refund adjustment" : "e.g. Monthly top-up"}
                className="mt-1 border-white/10 bg-white/5 text-cream"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogMode(null)} className="text-cream/50">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!amount || Number(amount) <= 0}
            >
              {dialogMode === "topup" ? "Add credits" : dialogMode === "deduction" ? "Deduct credits" : "Set balance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  )
}

function SortHeader({
  label,
  column,
  current,
  direction,
  onSort,
  className = "",
}: {
  label: string
  column: string
  current: string
  direction: string
  onSort: (column: string) => void
  className?: string
}) {
  const active = current === column
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40 transition-colors hover:text-cream/70 ${className}`}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (
          <svg className="size-3" viewBox="0 0 12 12" fill="currentColor">
            {direction === "asc" ? (
              <path d="M6 3L10 8H2L6 3Z" />
            ) : (
              <path d="M6 9L2 4H10L6 9Z" />
            )}
          </svg>
        )}
      </span>
    </th>
  )
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  )
}
